import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DashboardCalendar from '../components/DashboardCalendar';
import WeatherCard from '../components/WeatherCard';
import { ROUTE_PATHS } from '../config/navigation';
import { EXTERNAL_APP_LINKS } from '../config/organization';
import {
  getSelfServiceContentFallback,
  hydrateSelfServiceContent
} from '../data/selfServiceContent';
import { hydrateDocumentColumns, sortFeaturedFirst } from '../data/dashboardContent';

const fallbackAnnouncements = [
  {
    title: 'İş Sağlığı ve Güvenliği duyurusu',
    createdAt: new Date().toISOString(),
    author: { firstName: 'İK', lastName: 'Departmanı' }
  },
  {
    title: 'Koloğlu Holding duyurusu',
    createdAt: new Date().toISOString(),
    author: { firstName: 'Kurumsal', lastName: 'İletişim' }
  }
];

const getCurrentLunchMenu = (menuItems) => {
  if (!menuItems?.length) return null;

  const now = new Date();
  const dateKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
  const weekday = now.toLocaleDateString('tr-TR', { weekday: 'long' });

  return (
    menuItems.find((item) => item.date === dateKey) ||
    menuItems.find(
      (item) =>
        item.day?.toLocaleLowerCase('tr-TR') === weekday.toLocaleLowerCase('tr-TR')
    ) ||
    menuItems[0]
  );
};

const formatFeedDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : '';

const DashboardSkeleton = () => (
  <div className="corporate-dashboard dashboard-skeleton" aria-label="Anasayfa yükleniyor">
    <div className="dashboard-skeleton-line is-wide" />
    <div className="dashboard-skeleton-line" />
    <div className="dashboard-skeleton-panel" />
  </div>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [selfServiceContent, setSelfServiceContent] = useState(() =>
    getSelfServiceContentFallback()
  );
  const [documentColumns, setDocumentColumns] = useState(() => hydrateDocumentColumns());
  const [departments, setDepartments] = useState([]);
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryDepartment, setDirectoryDepartment] = useState('');

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch (error) {
      return {};
    }
  }, []);

  const fullName =
    [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || 'Çalışan';

  const today = useMemo(() => {
    const date = new Date();
    return {
      day: date.toLocaleDateString('tr-TR', { weekday: 'long' }),
      date: date.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    };
  }, []);

  const activeCompanyId = useMemo(
    () =>
      currentUser.role === 'super_admin'
        ? localStorage.getItem('activeCompanyId') || ''
        : currentUser.companyId || '',
    [currentUser]
  );

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [response, managedContent, departmentData] = await Promise.all([
          api.getCounts(),
          api.getManagedContent(),
          api.getDepartments().catch(() => ({ departments: [] }))
        ]);
        setAnnouncements(
          response.data?.latestAnnouncements?.length
            ? response.data.latestAnnouncements
            : fallbackAnnouncements
        );
        if (managedContent?.success) {
          const source = managedContent.data?.selfService || {};
          const now = new Date();
          const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          setSelfServiceContent(
            hydrateSelfServiceContent({
              ...source,
              lunchMenu: source.lunchMenus?.[monthKey] || source.lunchMenu || []
            })
          );
          setDocumentColumns(hydrateDocumentColumns(managedContent.data?.dashboard?.documentColumns));
        } else {
          setSelfServiceContent(getSelfServiceContentFallback());
          setDocumentColumns(hydrateDocumentColumns());
        }
        setDepartments(departmentData?.departments || []);
      } catch (error) {
        setAnnouncements(fallbackAnnouncements);
        setSelfServiceContent(getSelfServiceContentFallback());
        setDocumentColumns(hydrateDocumentColumns());
        setDepartments([]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [activeCompanyId]);

  const handleDirectorySearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (directorySearch.trim()) params.set('arama', directorySearch.trim());
    if (directoryDepartment) params.set('departman', directoryDepartment);
    const query = params.toString();
    navigate(`${ROUTE_PATHS.directory}${query ? `?${query}` : ''}`);
  };

  const currentLunchMenu = getCurrentLunchMenu(selfServiceContent.lunchMenu);

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="corporate-dashboard">
      <header className="corporate-dashboard-header">
        <div className="corporate-dashboard-greeting">
          <span className="corporate-dashboard-mark" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </span>
          <div>
            <p>Hoş geldiniz</p>
            <h1>{fullName}</h1>
            <time>
              {today.date}, <span>{today.day}</span>
            </time>
          </div>
        </div>
        <WeatherCard variant="compact" />
      </header>

      <nav className="corporate-dashboard-access" aria-label="Hızlı erişim">
        <div className="corporate-dashboard-access-title">
          <span>Hızlı Erişim</span>
          <small>Kurumsal uygulamalar</small>
        </div>
        <div className="corporate-dashboard-access-links">
          {EXTERNAL_APP_LINKS.map((app) => (
            <a key={app.id} href={app.url} target="_blank" rel="noopener noreferrer">
              <span className="corporate-dashboard-app-icon" aria-hidden="true">
                <i className={app.icon} />
              </span>
              <span>
                <strong>{app.label}</strong>
                <small>{app.description}</small>
              </span>
              <i className="bx bx-link-external" aria-hidden="true" />
            </a>
          ))}
        </div>
      </nav>

      <section className="corporate-dashboard-directory" aria-labelledby="dashboard-directory-title">
        <div>
          <span className="corporate-dashboard-section-icon">
            <i className="bx bx-group" aria-hidden="true" />
          </span>
          <div>
            <h2 id="dashboard-directory-title">Çalışan Rehberi</h2>
            <p>İsim, unvan veya departman ile arayın.</p>
          </div>
        </div>
        <form onSubmit={handleDirectorySearch}>
          <label>
            <i className="bx bx-search" aria-hidden="true" />
            <input
              value={directorySearch}
              onChange={(event) => setDirectorySearch(event.target.value)}
              placeholder="Çalışan ara"
              aria-label="Çalışan rehberinde ara"
            />
          </label>
          <div className="corporate-dashboard-directory-actions">
            <label>
              <i className="bx bx-buildings" aria-hidden="true" />
              <select
                value={directoryDepartment}
                onChange={(event) => setDirectoryDepartment(event.target.value)}
                aria-label="Departman seç"
              >
                <option value="">Tüm departmanlar</option>
                {departments.map((department) => (
                  <option key={department} value={department}>{department}</option>
                ))}
              </select>
            </label>
            <button type="submit">Ara</button>
          </div>
        </form>
      </section>

      <div className="corporate-dashboard-workspace">
        <section className="corporate-dashboard-section corporate-dashboard-calendar">
          <header>
            <div>
              <span className="corporate-dashboard-section-icon">
                <i className="bx bx-calendar" aria-hidden="true" />
              </span>
              <h2>Takvim</h2>
            </div>
            <button type="button" onClick={() => navigate(ROUTE_PATHS.events)}>
              Takvimi Aç
              <i className="bx bx-chevron-right" aria-hidden="true" />
            </button>
          </header>
          <DashboardCalendar />
        </section>

        <section className="corporate-dashboard-section corporate-dashboard-news">
          <header>
            <div>
              <span className="corporate-dashboard-section-icon">
                <i className="bx bx-volume-full" aria-hidden="true" />
              </span>
              <h2>Duyuru ve Haberler</h2>
            </div>
            <button type="button" onClick={() => navigate(ROUTE_PATHS.announcements)}>
              Tümünü Gör
              <i className="bx bx-chevron-right" aria-hidden="true" />
            </button>
          </header>

          <ol className="corporate-dashboard-news-list">
            {announcements.slice(0, 4).map((item, index) => (
              <li key={item._id || index}>
                <button type="button" onClick={() => navigate(ROUTE_PATHS.announcements)}>
                  <span className="corporate-dashboard-news-dot" aria-hidden="true" />
                  <span>
                    <strong>{item.title}</strong>
                    <small>
                      {formatFeedDate(item.createdAt || item.publishDate)}
                      {item.author?.firstName
                        ? ` · ${item.author.firstName} ${item.author.lastName || ''}`
                        : ''}
                    </small>
                  </span>
                  <i className="bx bx-chevron-right" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <div className="corporate-dashboard-resources">
        <section className="corporate-dashboard-section corporate-dashboard-documents">
          <header>
            <div>
              <span className="corporate-dashboard-section-icon">
                <i className="bx bx-folder" aria-hidden="true" />
              </span>
              <h2>Dokümanlar</h2>
            </div>
            <button type="button" onClick={() => navigate(ROUTE_PATHS.documents)}>
              Tümünü Gör
              <i className="bx bx-chevron-right" aria-hidden="true" />
            </button>
          </header>
          <div className="corporate-dashboard-document-grid">
            {documentColumns.map((column) => (
              <article key={column.key}>
                <button type="button" onClick={() => navigate(column.path)}>
                  <i className={column.icon} aria-hidden="true" />
                  <strong>{column.title}</strong>
                  <i className="bx bx-chevron-right" aria-hidden="true" />
                </button>
                <ul>
                  {sortFeaturedFirst(column.links).map((link) => (
                    <li key={link.label}>
                      <button type="button" onClick={() => navigate(link.path)}>{link.label}</button>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="corporate-dashboard-section corporate-dashboard-menu">
          <header>
            <div>
              <span className="corporate-dashboard-section-icon">
                <i className="bx bxs-dish" aria-hidden="true" />
              </span>
              <h2>Yemekhane Menüsü</h2>
            </div>
            <button type="button" onClick={() => navigate(ROUTE_PATHS.cafeteria)}>
              Menüyü aç
              <i className="bx bx-chevron-right" aria-hidden="true" />
            </button>
          </header>
          {currentLunchMenu ? (
            <div className="corporate-dashboard-menu-content">
              <div>
                <strong>{currentLunchMenu.day}</strong>
                {currentLunchMenu.serviceWindow && <span>{currentLunchMenu.serviceWindow}</span>}
              </div>
              <ul>
                {[
                  currentLunchMenu.soup,
                  currentLunchMenu.main,
                  currentLunchMenu.side,
                  currentLunchMenu.extra
                ]
                  .filter(Boolean)
                  .map((dish, index) => (
                    <li key={`${dish}-${index}`}>
                      <i className="bx bxs-circle" aria-hidden="true" />
                      <span>{dish}</span>
                    </li>
                  ))}
              </ul>
            </div>
          ) : (
            <p className="corporate-dashboard-empty">Menü bilgisi yakında paylaşılacak.</p>
          )}
        </section>
      </div>

    </div>
  );
};

export default DashboardPage;
